import { getOperationName } from 'apollo-utilities';
import {
  TAG_PRINT_BATCH_DETAIL_QUERY,
  TAG_PRINT_BATCH_LIST_QUERY,
  UPSERT_TAG_PRINT_BATCH_MUTATION,
} from '@/components/tag-print-batch-queries';
import {
  TagPrint_UpsertBatchMutation,
  TagPrint_UpsertBatchMutationVariables,
} from '@/types';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback } from 'react';
import { useItemQueryVariables } from './use-item-query-variables';
import { useMutation } from '@apollo/client';

export const useSaveTagPrintBatch = () => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const itemQueryVariables = useItemQueryVariables();

  const [upsertTagPrintBatch, { error, loading }] = useMutation<
    TagPrint_UpsertBatchMutation,
    TagPrint_UpsertBatchMutationVariables
  >(UPSERT_TAG_PRINT_BATCH_MUTATION, {
    refetchQueries: [getOperationName(TAG_PRINT_BATCH_LIST_QUERY) as string],
  });

  const createTagPrintBatch = useCallback(
    async (printTagTemplateUUID: string) => {
      return await upsertTagPrintBatch({
        variables: {
          ...itemQueryVariables,
          printTagTemplateUUID,
          restaurantUUID,
          supplierUUID,
        },
      });
    },
    [itemQueryVariables, restaurantUUID, supplierUUID, upsertTagPrintBatch],
  );

  const updateTagPrintBatch = useCallback(
    async (
      inputs: Pick<
        TagPrint_UpsertBatchMutationVariables,
        'items' | 'name' | 'printTagBatchUUID' | 'printTagTemplateUUID'
      >,
    ) => {
      return await upsertTagPrintBatch({
        awaitRefetchQueries: true,
        refetchQueries: [
          getOperationName(TAG_PRINT_BATCH_LIST_QUERY) as string,
          getOperationName(TAG_PRINT_BATCH_DETAIL_QUERY) as string,
        ],
        variables: {
          ...itemQueryVariables,
          ...inputs,
          restaurantUUID,
          supplierUUID,
        },
      });
    },
    [itemQueryVariables, restaurantUUID, supplierUUID, upsertTagPrintBatch],
  );

  return {
    createTagPrintBatch,
    error,
    loading,
    updateTagPrintBatch,
  };
};
